import * as React from 'react';

import type { Feature } from '../features';
import { UpsellDialog } from './UpsellDialog';

/**
 * The feature is the whole request. An earlier signature took an options bag carrying a
 * headline `metric` and a `note`; no call site ever passed either, and no query produces
 * the metric, so the dialog was shaped around a value that never arrived.
 */
type OpenUpsell = (feature: Feature) => void;

const UpsellContext = React.createContext<OpenUpsell | null>(null);

type DialogState = { feature: Feature } | null;

/**
 * Owns the single upsell dialog for the whole app.
 *
 * One dialog, mounted once, opened from anywhere. The alternative — every locked control
 * rendering its own — is how you end up with two upsells stacked on one screen, which is
 * the fastest way to make an agency feel nickel-and-dimed.
 */
export function UpsellProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = React.useState<DialogState>(null);

  /**
   * The control that was focused when the upsell was asked for, and where focus goes back to.
   *
   * The dialog is UNMOUNTED on close rather than left open={false}, deliberately: unmounting is
   * what discards its "request sent" confirmation, so the next lock opens on the pitch instead of
   * a stale receipt. The cost is that Radix's own focus restoration has nothing left to restore
   * to and focus lands on <body> — one Tab from leaving the panel the user opened it from. So the
   * provider remembers the trigger itself and restores it in an effect, after the unmount commits.
   */
  const triggerRef = React.useRef<HTMLElement | null>(null);
  const restoreRef = React.useRef<HTMLElement | null>(null);

  const openUpsell = React.useCallback<OpenUpsell>((feature) => {
    triggerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setState({ feature });
  }, []);

  React.useEffect(() => {
    if (state !== null) return;
    const element = restoreRef.current;
    restoreRef.current = null;
    // `isConnected`: a lock inside a panel that closed behind the dialog is gone, and focusing a
    // detached node throws focus to <body> anyway.
    if (element?.isConnected) element.focus();
  }, [state]);

  return (
    <UpsellContext.Provider value={openUpsell}>
      {children}
      {state ? (
        <UpsellDialog
          open
          onOpenChange={(open) => {
            if (open) return;
            restoreRef.current = triggerRef.current;
            triggerRef.current = null;
            setState(null);
          }}
          feature={state.feature}
        />
      ) : null}
    </UpsellContext.Provider>
  );
}

/**
 * Opens the upsell dialog. Safe outside the provider — returns a no-op rather than
 * throwing, so a locked control rendered in isolation (Storybook, a unit test) degrades
 * to an inert button instead of crashing the tree.
 */
export function useUpsell(): OpenUpsell {
  const openUpsell = React.useContext(UpsellContext);
  return openUpsell ?? noop;
}

const noop: OpenUpsell = () => {};
